import { queryOptions, useMutation } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

import type { AnimalDto } from './index';

export type BreedOption = {
  id: string;
  name: string;
  code: string;
};

export type AccountOption = {
  id: string;
  name: string;
  code: string | null;
};

export type YardOption = {
  id: string;
  name: string;
};

export type PenOption = {
  id: string;
  name: string;
  yard_id: string | null;
};

export type CreateAnimalInput = {
  animalType: number;
  name?: string | null;
  tag?: string | null;
  tattoo?: string | null;
  metalTag?: string | null;
  registration?: string | null;
  pedigreeName?: string | null;
  breedId?: string | null;
  ownerId?: string | null;
  yardId?: string | null;
  penId?: string | null;
  colorId?: string | null;
  sex?: number | null;
  birth?: string | null;
  weight?: number | null;
  programCategory?: string | null;
  opuProgram?: string | null;
  pregnant?: number | null;
  ivfStatusId?: string | null;
  ocvStatusId?: string | null;
  recipientStatusId?: string | null;
  received?: string | null;
  shipOn?: string | null;
  /** Two-letter US state code. */
  origin?: string | null;
  /** Donor only. */
  hairSample?: boolean | null;
  /** Donor only, tog_heifercow picklist value. */
  heiferCow?: number | null;
  /** Recipient only. */
  lastDrugUsedId?: string | null;
  /** Recipient only, "Original Owner" (tog_Client) picklist value. */
  client?: number | null;
  /** Required to save over possible duplicates; kept in the audit history. */
  duplicateOverrideReason?: string | null;
};

export type AnimalPayload = {
  code: number;
  success: boolean;
  message: string | null;
  data: unknown;
  errors: Record<string, string> | null;
};

/** An existing active animal that a create or edit may duplicate (409). */
export type PossibleDuplicate = {
  animal: AnimalDto;
  /** Input field names that matched, e.g. metalTag, tag. */
  matchedOn: string[];
};

export const getPossibleDuplicates = (
  payload: AnimalPayload,
): PossibleDuplicate[] => {
  if (payload.code !== 409) return [];
  const data = payload.data as { possibleDuplicates?: unknown } | null;
  return Array.isArray(data?.possibleDuplicates)
    ? (data.possibleDuplicates as PossibleDuplicate[])
    : [];
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function createAnimal(
  token: AccessToken,
  input: CreateAnimalInput,
): Promise<AnimalPayload> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/animal`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(token),
      body: JSON.stringify(input),
    });
    return await readPayload(response);
  } catch (error) {
    return failedPayload((error as Error).message);
  }
}

const failedPayload = (message: string): AnimalPayload => ({
  code: 0,
  success: false,
  message,
  data: null,
  errors: null,
});

const isAnimalPayload = (value: unknown): value is AnimalPayload =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as AnimalPayload).success === 'boolean';

/**
 * Unhandled server errors come back as an empty body, HTML or ProblemDetails
 * rather than an AnimalPayload; turn those into a failed payload so the caller
 * can show the error instead of failing silently.
 */
async function readPayload<T extends AnimalPayload | AnimalPayload[]>(
  response: Response,
): Promise<T | AnimalPayload> {
  const text = await response.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : undefined;
  } catch {
    body = undefined;
  }
  if (isAnimalPayload(body)) return body;
  if (Array.isArray(body) && body.every(isAnimalPayload)) return body as T;
  const title =
    typeof body === 'object' && body !== null
      ? (body as { title?: unknown }).title
      : undefined;
  return failedPayload(
    typeof title === 'string' && title
      ? `${title} (HTTP ${response.status})`
      : `The server could not save the animal (HTTP ${response.status}).`,
  );
}

/**
 * POST /api/v1/animal/bulk. One payload per input, in order; rows with possible
 * duplicates come back as 409 payloads and are not saved. A request-level
 * failure is applied to every input.
 */
export async function createAnimals(
  token: AccessToken,
  inputs: CreateAnimalInput[],
): Promise<AnimalPayload[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/animal/bulk`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(token),
      body: JSON.stringify(inputs),
    });
    const result = await readPayload<AnimalPayload[]>(response);
    if (Array.isArray(result)) return result;
    return inputs.map(() => result);
  } catch (error) {
    const payload = failedPayload((error as Error).message);
    return inputs.map(() => payload);
  }
}

export function breedsQueryOptions(
  token: AccessToken,
  { search }: { search?: string } = {},
) {
  return queryOptions({
    queryKey: ['lookups', 'breeds', { search }],
    enabled: !!token,
    async queryFn() {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const query = params.toString();

      const response = await fetch(
        `${API_BASE_URL}/api/v1/lookups/breeds${query ? `?${query}` : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(token),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as BreedOption[];
    },
  });
}

export function accountsQueryOptions(
  token: AccessToken,
  { search }: { search?: string } = {},
) {
  return queryOptions({
    queryKey: ['lookups', 'accounts', { search }],
    enabled: !!token,
    async queryFn() {
      const params = new URLSearchParams();
      const trimmedSearch = search?.trim();
      if (trimmedSearch) params.set('search', trimmedSearch);
      const query = params.toString();

      const response = await fetch(
        `${API_BASE_URL}/api/v1/account/autocomplete${query ? `?${query}` : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(token),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as AccountOption[];
    },
  });
}

export function yardsQueryOptions(
  token: AccessToken,
  { search }: { search?: string } = {},
) {
  return queryOptions({
    queryKey: ['lookups', 'yards', { search }],
    enabled: !!token,
    async queryFn() {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const query = params.toString();

      const response = await fetch(
        `${API_BASE_URL}/api/v1/lookups/yards${query ? `?${query}` : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(token),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as YardOption[];
    },
  });
}

export function pensQueryOptions(
  token: AccessToken,
  { search }: { search?: string } = {},
) {
  return queryOptions({
    queryKey: ['lookups', 'pens', { search }],
    enabled: !!token,
    async queryFn() {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const query = params.toString();

      const response = await fetch(
        `${API_BASE_URL}/api/v1/lookups/pens${query ? `?${query}` : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(token),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return (await response.json()) as PenOption[];
    },
  });
}

export const useCreateAnimal = () => {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (input: CreateAnimalInput) => createAnimal(token, input),
  });
};

export const useCreateAnimals = () => {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (inputs: CreateAnimalInput[]) => createAnimals(token, inputs),
  });
};

export const OPTION_SET_OBJECT_TYPES = {
  donor: 10012,
  calf: 10008,
  recipient: 10023,
} as const;

export type OptionSetAttribute = 'tog_opuprogram' | 'tog_category';

type StringMapItem = { key: number; value: string | null };

export function optionSetQueryOptions(
  token: AccessToken,
  objectTypeCode: number,
  attributeName: OptionSetAttribute,
) {
  return queryOptions({
    queryKey: ['lookups', 'string-map', objectTypeCode, attributeName],
    enabled: !!token,
    staleTime: 30 * 60 * 1000,
    async queryFn() {
      const params = new URLSearchParams({
        objectTypeCode: String(objectTypeCode),
        name: attributeName,
      });
      const response = await fetch(
        `${API_BASE_URL}/api/v1/lookups/string-map?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: getHeaders(token),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const items = (await response.json()) as StringMapItem[];
      // The API writes by label, so labels are the option values.
      const seen = new Set<string>();
      return items
        .map((item) => item.value?.trim() ?? '')
        .filter((label) => {
          if (!label || seen.has(label)) return false;
          seen.add(label);
          return true;
        });
    },
  });
}

export type LookupOption = { id: string; name: string | null };

async function getLookup<T>(
  token: AccessToken,
  path: string,
  params: URLSearchParams,
) {
  const query = params.toString();
  const response = await fetch(
    `${API_BASE_URL}/api/v1/lookups/${path}${query ? `?${query}` : ''}`,
    { method: 'GET', credentials: 'include', headers: getHeaders(token) },
  );
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as T;
}

const searchParams = (search?: string) => {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  return params;
};

export function drugsQueryOptions(
  token: AccessToken,
  { search }: { search?: string } = {},
) {
  return queryOptions({
    queryKey: ['lookups', 'drugs', { search }],
    enabled: !!token,
    queryFn: () =>
      getLookup<LookupOption[]>(token, 'drugs', searchParams(search)),
  });
}

export type DiagnosisOption = LookupOption & {
  drugId: string | null;
  drugName: string | null;
  dose: number | null;
  unit: string | null;
  defaultProcedureId: string | null;
};

export function diagnosesQueryOptions(
  token: AccessToken,
  { search }: { search?: string } = {},
) {
  return queryOptions({
    queryKey: ['lookups', 'diagnoses', { search }],
    enabled: !!token,
    queryFn: () =>
      getLookup<DiagnosisOption[]>(token, 'diagnoses', searchParams(search)),
  });
}

export function colorsQueryOptions(
  token: AccessToken,
  { search }: { search?: string } = {},
) {
  return queryOptions({
    queryKey: ['lookups', 'colors', { search }],
    enabled: !!token,
    queryFn: () =>
      getLookup<LookupOption[]>(token, 'colors', searchParams(search)),
  });
}

export type AnimalStatusKind = 'recipient' | 'ocv';

export function animalStatusOptionsQueryOptions(
  token: AccessToken,
  kind: AnimalStatusKind,
) {
  return queryOptions({
    queryKey: ['lookups', 'animal-status-options', kind],
    enabled: !!token,
    staleTime: 30 * 60 * 1000,
    queryFn: () =>
      getLookup<LookupOption[]>(
        token,
        'animal-status-options',
        new URLSearchParams({ kind }),
      ),
  });
}

export type KeyedStringMapAttribute =
  | 'tog_sex'
  | 'tog_client'
  | 'tog_heifercow';

/** Picklist options keyed by their stored value, for fields written by value. */
export function keyedStringMapQueryOptions(
  token: AccessToken,
  objectTypeCode: number,
  attributeName: KeyedStringMapAttribute,
) {
  return queryOptions({
    queryKey: ['lookups', 'string-map-keyed', objectTypeCode, attributeName],
    enabled: !!token,
    staleTime: 30 * 60 * 1000,
    async queryFn() {
      const items = await getLookup<StringMapItem[]>(
        token,
        'string-map',
        new URLSearchParams({
          objectTypeCode: String(objectTypeCode),
          name: attributeName,
        }),
      );
      const seen = new Set<number>();
      return items
        .filter((item) => {
          if (seen.has(item.key) || !item.value?.trim()) return false;
          seen.add(item.key);
          return true;
        })
        .map((item) => ({
          value: String(item.key),
          label: item.value!.trim(),
        }));
    },
  });
}
